import React, { memo } from 'react';
import type { CardProps } from '../../types';

/**
 * Card - A versatile card container with multiple variants
 * 
 * @example
 * <Card>Basic card content</Card>
 * <Card variant="elevated" interactive>Clickable elevated card</Card>
 * <Card variant="glass" highlighted>Glass effect with highlight</Card>
 */

export type CardVariant = 'default' | 'elevated' | 'glass' | 'outlined';

export interface ExtendedCardProps extends CardProps {
  /** Visual variant */
  variant?: CardVariant;
  /** Add hover effects for interactive cards */
  interactive?: boolean;
}

const variantClasses: Record<CardVariant, string> = {
  default: `
    bg-white dark:bg-dark-card
    shadow-lg dark:shadow-slate-900/50
  `,
  elevated: `
    bg-white dark:bg-dark-card
    shadow-xl dark:shadow-slate-900/70
  `,
  glass: `
    glass
    shadow-xl
    ring-1 ring-white/80 dark:ring-dark-border
  `,
  outlined: `
    bg-white dark:bg-dark-card
    border-2 border-gray-200 dark:border-dark-border
    shadow-sm
  `,
};

export const Card: React.FC<ExtendedCardProps> = memo(({
  children,
  className = '',
  onClick,
  highlighted = false,
  variant = 'default',
  interactive,
}) => {
  // Auto-detect interactive if onClick is provided
  const isInteractive = interactive ?? !!onClick;
  
  const baseClasses = `
    rounded-2xl sm:rounded-3xl
    p-4 sm:p-6
    transition-all duration-300
  `;

  const interactiveClasses = isInteractive
    ? `
      cursor-pointer
      card-interactive
      active:opacity-90
    `
    : '';

  const highlightClasses = highlighted
    ? `
      ring-4 ring-kid-yellow
      shadow-xl shadow-yellow-200 dark:shadow-yellow-900/30
    `
    : '';

  return (
    <div
      onClick={onClick}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${interactiveClasses}
        ${highlightClasses}
        ${className}
      `}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';
